/**
 * The chat-universal step list — a collapsible card pinned above the composer.
 *
 * Spec 084 step 1: the checklist existed only as a CoWork side panel, so normal
 * chat, Super Agent and single agents never saw one. This is the surface that
 * makes it universal; it renders wherever the composer renders, on any workspace
 * type, and stands down when the CoWork panel is already showing the same list.
 *
 * Renders nothing when there is no plan — which is the correct, common state for
 * a trivial request, so it must cost nothing and flash nothing — and nothing once
 * the turn ends, because a settled checklist above the composer is no longer
 * telling the user anything they need (see `useLivePlan`).
 *
 * @module components/chat/plan/plan-checklist-card
 */

import { useRef, useState } from "react";
import { ChevronDown, ListChecks } from "lucide-react";

import { cn } from "@/lib/utils";
import { useLivePlan } from "@/store/plan-store";

import { PlanTimeline } from "./plan-timeline";
import { usePlanAutoScroll } from "./use-plan-autoscroll";

interface PlanChecklistCardProps {
  /**
   * Set when another surface is already showing this list — in practice the
   * CoWork Context panel. Showing the same checklist twice on one screen reads
   * as a bug, and the caller is the only place that knows whether that panel is
   * mounted (it yields to the artifact canvas), so the condition is passed in
   * rather than re-derived here and left to drift.
   */
  suppressed?: boolean;
  className?: string;
}

export function PlanChecklistCard({ suppressed = false, className }: PlanChecklistCardProps) {
  const plan = useLivePlan();
  const [open, setOpen] = useState(true);

  const tasks = plan?.tasks ?? [];
  const completed = tasks.filter((t) => t.status === "completed").length;
  const activeIdx = tasks.findIndex((t) => t.status === "in_progress");

  /**
   * Keep the moving step in view as work advances — on a long plan the "you are
   * here" node otherwise scrolls out of the capped area and the card looks
   * frozen while the agent is clearly still working.
   */
  const scrollRef = useRef<HTMLDivElement>(null);
  usePlanAutoScroll(scrollRef, tasks, open);

  if (suppressed || tasks.length === 0) return null;

  const total = tasks.length;
  const activeTitle = activeIdx >= 0 ? tasks[activeIdx]?.title : undefined;

  return (
    <div
      className={cn(
        "mb-2 rounded-xl border border-border/60 bg-background/80 px-3 py-2 shadow-sm backdrop-blur",
        /**
         * Entrance, not a pop. The card mounts mid-turn, directly above the
         * composer, and an un-animated mount reads as a layout glitch — the
         * composer jumps and the card looks like it arrived late rather than
         * on cue. Same idiom as the follow-up suggestion strip that occupies
         * this exact slot, so the two never look like different systems.
         *
         * `fill-mode-both` holds the first frame so nothing flashes at full
         * opacity before the animation starts.
         */
        "animate-in fade-in slide-in-from-bottom-2 fill-mode-both duration-300 ease-out",
        className,
      )}
      data-testid="plan-checklist"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 text-left"
      >
        <ListChecks className="size-4 shrink-0 text-secondary" />
        <span className="text-[13px] font-semibold text-foreground">Plan</span>
        {/* Collapsed, the active step is the one thing worth a line of space. */}
        {!open && activeTitle && (
          <span className="min-w-0 flex-1 truncate text-[12px] text-muted-foreground">{activeTitle}</span>
        )}
        <span
          className={cn(
            "text-[11px] font-medium tabular-nums text-muted-foreground",
            open ? "ml-auto" : "shrink-0",
          )}
        >
          {completed}/{total}
        </span>
        <ChevronDown
          className={cn(
            "size-4 shrink-0 text-muted-foreground transition-transform",
            !open && "-rotate-90",
          )}
        />
      </button>

      {open && (
        <>
          <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-success transition-all duration-500 ease-out"
              style={{ width: `${total ? (completed / total) * 100 : 0}%` }}
            />
          </div>
          {/**
           * `overflow-y` also clips horizontally, and the active node's ring
           * sits 4px outside its 18px circle — so without side padding the
           * "you are here" halo gets sliced off against the scroll box. Most
           * visible in a narrow chat column (artifact canvas open).
           */}
          <div
            ref={scrollRef}
            className="scrollbar-hide mt-2 max-h-[22vh] overflow-y-auto px-1.5 py-1 animate-in fade-in fill-mode-both duration-200 ease-out"
          >
            <PlanTimeline tasks={tasks} />
          </div>
        </>
      )}
    </div>
  );
}
